import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { isSameDay } from 'date-fns';

import {
  AttributeType,
  Consultation,
  FollowUpAction,
  FollowUpOutput,
  FollowUpSelector,
  FormField,
  InputType,
  Patient,
  Weight,
} from '@app/core/models';
import { ApiManagerService, LoaderService, Logger, ModalService, NutritionService, VetService } from '@app/core/services';

import { minDateValidator, pastDateValidator } from '@app/shared/directives';
import { Constants, DataSubscriber, Helper, VetPreferences } from '@app/shared/utils';
import { FollowUpSelectorPopinComponent } from '@app/shared/components/follow-up-selector-popin/follow-up-selector-popin.component';
import { MeasureHelper } from '@app/shared/utils/static-helpers/measure-helper';
import { CoreFacade } from '@app/store/core/core.facade';
import { RCAlertType } from '@rc/ui';

@Component({
  selector: 'app-follow-up',
  templateUrl: './follow-up.component.html',
  styleUrls: ['./follow-up.component.scss'],
  providers: [DatePipe],
})
export class FollowUpComponent implements OnInit, OnDestroy {
  Constants = Constants;

  @Input() patient: Patient;
  @Input() lastConsultation: Consultation;

  @Output()
  followUpOutput: EventEmitter<FollowUpOutput> = new EventEmitter<FollowUpOutput>();

  /** Enter weight screen (first state) **/
  enterWeightFields: { [id: string]: FormField };
  enterWeightForm: UntypedFormGroup;

  minWeight: string;
  maxWeight: string;
  weightSuffix = MeasureHelper.measureUnitToSuffix(VetPreferences.currentBigMeasurementUnit);
  isLoading = false;
  alertType = RCAlertType;
  error: string;

  private _destroyed$ = new Subject();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private modal: ModalService,
    private loaderService: LoaderService,
    private apiManagerService: ApiManagerService,
    private nutritionService: NutritionService,
    private vetService: VetService,
    private logger: Logger,
    private coreFacade: CoreFacade
  ) {}

  ngOnInit(): void {
    this._setAttributes();
    this._createForm();
    this._setCommunicator();
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  /**
   * Function checking for date change and make Api call for update next visit date
   */
  nextVisitDateUpdate() {
    this.isLoading = true;
    const updateData = Helper.buildVisitDataUpdate('next', this.enterWeightForm.get('visitDate').value);
    this.vetService
      .updateConsultationVisits(updateData, this.patient.id, this.lastConsultation.id)
      .pipe(take(1), takeUntil(this._destroyed$))
      .subscribe((lastConsultation) => {
        this.lastConsultation = lastConsultation;
        this._setMinAndMax(lastConsultation);
        this.isLoading = false;
        if (
          isSameDay(
            Helper.parseDate(lastConsultation.nextVisit.expected.visitDateTime),
            Helper.parseDate(lastConsultation.visit.visitDateTime)
          )
        ) {
          this.coreFacade.setAlert({
            message: $localize`:@@next_visit_date_error_msg:`,
            alertType: RCAlertType.WARNING,
          });
        }
      });
  }

  onSubmitConsultation(event): void {
    event.preventDefault();
    const { visitDate, weight } = this.enterWeightForm.value;
    this.loaderService.startLoader(true);
    const weightObj: Weight = {
      measure: weight,
      measureUnit: VetPreferences.currentBigMeasurementUnit,
      weightDate: visitDate,
    };

    const recommendationProgramBody = this.apiManagerService.buildRecommendationProgramBody(this.patient, this.lastConsultation, weightObj);

    const recommendationPrograms = this.nutritionService.recommendationPrograms(recommendationProgramBody);

    DataSubscriber.subscribe(
      recommendationPrograms,
      (followUpSelector) => {
        this.loaderService.stopLoader(() => {
          if (followUpSelector.actions.length < 2) {
            this.selectUniqueAction(followUpSelector, weight, visitDate, this.lastConsultation);
          } else {
            this.openActionsPopin(followUpSelector, weight, visitDate, this.lastConsultation);
          }
        });
      },
      this._destroyed$,
      (error) => {
        this.logger.error(error);
        this.loaderService.stopLoader();
        this.error = $localize`:@@error_try-again-later:`;
      }
    );
  }

  /**
   * Function select default action
   */
  selectUniqueAction(followUpSelector: FollowUpSelector, weight: number, visitDate: Date, lastConsultation: Consultation) {
    const action: FollowUpAction = followUpSelector.actions[0];
    this.followUpOutput.emit({
      weight,
      visitDate,
      action,
      lastConsultation,
    });
  }

  /**
   * Function open popin when there is several actions
   */
  openActionsPopin(followUpSelector: FollowUpSelector, weight: number, visitDate: Date, lastConsultation: Consultation) {
    const modal = this.modal.open(FollowUpSelectorPopinComponent, {
      data: {
        followUpSelector,
        previousWeight: lastConsultation.visit.weight,
        currentWeightMeasure: weight,
        targetUnit: VetPreferences.currentBigMeasurementUnit,
      },
    });
    modal.afterClosed.pipe(take(1), takeUntil(this._destroyed$)).subscribe((action: FollowUpAction) => {
      if (action) {
        this.followUpOutput.emit({
          weight,
          visitDate,
          action,
          lastConsultation,
        });
      }
    });
  }

  resetFormWeight(): void {
    this.enterWeightForm.reset();
  }

  private _setAttributes() {
    this.enterWeightFields = {
      visitDate: {
        id: 'visitDate',
        type: AttributeType.Date,
        label: $localize`:@@follow-up_form-label-date:`,
        required: true,
        disableAutocomplete: true,
        inputType: InputType.Date,
      },
      weight: {
        id: 'weight',
        type: AttributeType.Weight,
        placeholder: $localize`:@@form-attribute_pet-weight:`,
        required: true,
        disableAutocomplete: true,
        inputType: InputType.Weight,
      },
    };
  }

  private _setCommunicator() {
    this.nextVisitDateUpdate();
  }

  private _setMinAndMax(lastConsultation) {
    if (lastConsultation.nextVisit && lastConsultation.nextVisit.expected) {
      const expectedWeight = lastConsultation.nextVisit.expected.weight;
      const localizeMeasure = (weight) => {
        return MeasureHelper.convertWeight(weight, expectedWeight.measureUnit, VetPreferences.currentBigMeasurementUnit).measure.toFixed(2);
      };
      this.minWeight = localizeMeasure(expectedWeight.minWeight);
      this.maxWeight = localizeMeasure(expectedWeight.maxWeight);
    }
  }

  private _createForm() {
    this.enterWeightForm = this.formBuilder.group({
      visitDate: [
        new Date(),
        [
          Validators.required,
          pastDateValidator,
          minDateValidator(this.lastConsultation.visit.visitDateTime),
          minDateValidator(this.patient.pet.birth.date),
        ],
      ],
      weight: [null, [Validators.required, Validators.max(VetPreferences.maxPetWeight)]],
    });
  }
}
